import moment from 'moment';
import * as R from 'ramda';
import uuid from 'uuid/v4';

import { WorkOrder } from '@atom/types/work';

// "grace" period after a work order busy time allowed before staring a gap
const MINUTES_BEFORE_GAP = 15;

export interface CalendarGap {
  id: string;
  plannedStartDate: number;
  dueDate: number;
  isGap: boolean;
  firstGap?: boolean;
}

// calculateGaps expects all workOrders to have
//   a dueDate on the same day.
//
// workOrders should be pre-sorted by plannedStartDate
//
// calculateGaps returns the same workOrders array with CalendarGaps inserted
export const calculateGaps = (
  initWorkOrders: WorkOrder[],
): (CalendarGap | WorkOrder)[] => {
  const noPlannedStartDateInOrders = !initWorkOrders.some(
    ({ plannedStartDate }) => !R.isNil(plannedStartDate),
  );

  // if workOrders empty, return empty array and exit
  if (R.isEmpty(initWorkOrders) || noPlannedStartDateInOrders) {
    return [];
  }

  const workOrders = initWorkOrders.filter(order => order?.plannedStartDate!!);

  // Start by getting the start ms for this day
  // Use the first workOrder dueDate since these should all be on the same day
  // - we are guaranteed that there is at least one order and all orders
  //   have both plannedStartDate and dueDate
  const firstOrder = workOrders[0];
  const startOfDay = moment(firstOrder.dueDate).startOf('day');
  const endOfDay = moment(firstOrder.dueDate).endOf('day');

  // Each day needs to track the first gap
  let gapIdx = 0;

  const getFirstGap = () => {
    gapIdx = gapIdx + 1;
    return gapIdx === 1;
  };

  // Keep track of end of current busy period between workOrders.reduce iterations
  const busy = {
    end: startOfDay.valueOf(),
  };

  // for initial gap, only add if first order starts MINUTES_BEFORE_GAP after 12am
  const initWorkOrdersAndGaps: any[] =
    firstOrder.plannedStartDate >=
    moment(startOfDay.valueOf()).add(MINUTES_BEFORE_GAP, 'minutes').valueOf()
      ? [
          {
            id: uuid(),
            plannedStartDate: startOfDay.valueOf(),
            dueDate: firstOrder.plannedStartDate,
            isGap: true,
            firstGap: getFirstGap(),
          },
        ]
      : [];

  const workOrdersAndGaps: (WorkOrder & CalendarGap)[] = workOrders.reduce(
    (acc, order, idx, orders) => {
      // Update busyRange to account for current order dueDate
      busy.end = R.max(order.dueDate, busy.end);

      // Compare order to the next plannedStartDate to know if we need a gap
      const nextOrder = orders[idx + 1];

      if (R.isNil(nextOrder)) {
        // This is the last order
        //   add final gap here if endDate is more
        //   than MINUTES_BEFORE_GAP ahead of EOD
        if (
          moment(order.dueDate).add(MINUTES_BEFORE_GAP, 'minutes') <= endOfDay
        ) {
          return [
            ...acc,
            order,
            {
              id: uuid(),
              plannedStartDate: order.dueDate,
              dueDate: endOfDay.valueOf(),
              isGap: true,
              firstGap: getFirstGap(),
            },
          ];
        }

        // End time was close enough to the end of the day that we don't need a final gap
        return [...acc, order];
      }

      // if the next order's plannedStartDate is more than MINUTES_BEFORE_GAP past
      // the current busy range end, we need to add a gap
      if (
        nextOrder.plannedStartDate >=
        moment(busy.end).add(MINUTES_BEFORE_GAP, 'minutes').valueOf()
      ) {
        const newGap: CalendarGap = {
          id: uuid(),
          plannedStartDate: busy.end,
          dueDate: nextOrder.plannedStartDate,
          isGap: true,
          firstGap: getFirstGap(),
        };

        // update busy.end for new busy period
        busy.end = nextOrder.plannedStartDate;

        // add gap!
        return [...acc, order, newGap];
      }

      // aww no gap... stay busy and keep on keeping on, fool!
      return [...acc, order];
    },
    initWorkOrdersAndGaps,
  );

  // if there is only one gap and its the last element
  // - set firstGap to false
  if (gapIdx === 1 && R.last(workOrdersAndGaps)?.isGap) {
    return workOrdersAndGaps.map(item => {
      return item?.firstGap ? { ...item, firstGap: false } : item;
    });
  }

  return workOrdersAndGaps;
};

export const tuesdayOrders: WorkOrder[] = [
  {
    id: uuid(),
    name: 'Fake Order 2',
    plannedStartDate: 1754406000000, // Tue, 8.5.2025 10:00:00 AM
    dueDate: 1754413200000, // Tue, 8.5.25 12:00:00 PM
    appointmentsEnabled: true,
    tasks: [],
    reopened: false,
    fields: [],
    priorityId: 1,
    statusId: '2',
  },
  {
    id: uuid(),
    name: 'Fake Order 3',
    plannedStartDate: 1754409600000, // Tue, 8.5.2025 11:00:00 AM
    dueDate: 1754411400000, // Tue, 8.5.25 11:30:00 AM
    appointmentsEnabled: true,
    tasks: [],
    reopened: false,
    fields: [],
    priorityId: 1,
    statusId: '3',
  },
  {
    id: uuid(),
    name: 'Fake Order 4',
    plannedStartDate: 1754416800000, // Tue, 8.5.2025 1:00:00 PM
    dueDate: 1754425800000, // Tue, 8.5.25 3:00:00 PM
    appointmentsEnabled: true,
    tasks: [],
    reopened: false,
    fields: [],
    priorityId: 1,
    statusId: '4',
  },
  {
    id: uuid(),
    name: 'Fake Order 5',
    plannedStartDate: 1754416800000, // Tue, 8.5.2025 1:00:00 PM
    dueDate: 1754425800000, // Tue, 8.5.25 3:00:00 PM
    appointmentsEnabled: true,
    tasks: [],
    reopened: false,
    fields: [],
    priorityId: 1,
    statusId: '4',
  },
  {
    id: uuid(),
    name: 'Fake Order 6',
    plannedStartDate: 1754420400000, // Tue, 8.5.2025 2:00:00 PM
    dueDate: 1754424000000, // Tue, 8.5.25 3:00:00 PM
    appointmentsEnabled: true,
    tasks: [],
    reopened: false,
    fields: [],
    priorityId: 1,
    statusId: '2',
  },
  {
    id: uuid(),
    name: 'Fake Order 7',
    plannedStartDate: 1754431200000, // Tue, 8.5.2025 5:00:00 PM
    dueDate: 1754434800000, // Tue, 8.5.2025 6:00:00 PM
    appointmentsEnabled: true,
    tasks: [],
    reopened: false,
    fields: [],
    priorityId: 1,
    statusId: '6',
  },
];
